import { ParkingLotTicket } from '@/hooks/useParkingLotMap';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Car, MapPin, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SpotWithTicket {
  id: string;
  label: string;
  ticketId?: string | null;
}

interface ParkingLotTicketPanelProps {
  tickets: ParkingLotTicket[];
  spots: SpotWithTicket[];
  selectedSpotId: string | null;
  onTicketDrop: (ticketId: string, spotId: string) => void;
  onUnassignTicket: (ticketId: string) => void;
}

export function ParkingLotTicketPanel({
  tickets,
  spots,
  selectedSpotId,
  onTicketDrop,
  onUnassignTicket,
}: ParkingLotTicketPanelProps) {
  const selectedSpot = spots.find(s => s.id === selectedSpotId);

  // Get all tickets currently assigned to spots
  const assignedTicketIds = new Set(spots.filter(s => s.ticketId).map(s => s.ticketId));
  
  // Unassigned tickets for the panel
  const unassignedTickets = tickets.filter(t => !assignedTicketIds.has(t.id));

  return (
    <div className="w-72 flex flex-col gap-4">
      {/* Selected Spot Info */}
      {selectedSpot && (
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              {selectedSpot.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {selectedSpot.ticketId ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Currently occupied</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => onUnassignTicket(selectedSpot.ticketId!)}
                >
                  <X className="h-3 w-3" />
                  Unassign Ticket
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Click a ticket below to assign
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Unassigned Tickets */}
      <Card className="flex-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Car className="h-4 w-4" />
            Unplaced Tickets
            <Badge variant="secondary" className="ml-auto">
              {unassignedTickets.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <div className="p-4 pt-0 space-y-2">
              {unassignedTickets.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  All tickets are assigned to spots
                </p>
              ) : (
                unassignedTickets.map(ticket => (
                  <button
                    key={ticket.id}
                    className={cn(
                      'w-full text-left p-3 rounded-lg border transition-colors',
                      'hover:bg-accent hover:border-accent-foreground/20',
                      selectedSpotId && !selectedSpot?.ticketId
                        ? 'cursor-pointer border-primary/50 bg-primary/5'
                        : 'cursor-default'
                    )}
                    onClick={() => {
                      if (selectedSpotId && !selectedSpot?.ticketId) {
                        onTicketDrop(ticket.id, selectedSpotId);
                      }
                    }}
                    disabled={!selectedSpotId || !!selectedSpot?.ticketId}
                  >
                    <div className="flex items-start gap-2">
                      <Car className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {ticket.vehicleInfo}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {ticket.customer_name}
                        </p>
                        <Badge
                          variant={
                            ticket.status === 'Completed'
                              ? 'default'
                              : ticket.status === 'Paused'
                              ? 'secondary'
                              : 'outline'
                          }
                          className="mt-1 text-xs"
                        >
                          {ticket.status}
                        </Badge>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
